import { useEffect, useState } from 'react';
import { type SupportedIntegrationProductSlugs } from '../constants';
import { useAuthContext } from '../contexts/auth';
import { useEnterprises } from '../contexts/enterprises';
import {
  getEnterpriseIntegrationTemplates,
  type IntegrationTemplate
} from '../api-client';
import type { ErrorOrNull } from '../../../typings/utils';
import type { IntegrationConnection } from '../utils/integrations-client/jira/connections';

type UseIntegrationWithTemplatesByProduct = {
  connections: IntegrationConnection[];
  integrationProductSlug: SupportedIntegrationProductSlugs;
};

const useTemplatesForConnections = ({
  connections,
  integrationProductSlug
}: UseIntegrationWithTemplatesByProduct) => {
  const [isTemplatesLoading, setIsTemplatesLoading] = useState(false);
  const [templates, setTemplates] = useState<IntegrationTemplate[]>([]);
  const [templatesError, setTemplatesError] = useState<ErrorOrNull>(null);

  const { user } = useAuthContext();

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const token = user!.token;
  const { activeEnterprise } = useEnterprises();

  useEffect(() => {
    if (!connections.length || !activeEnterprise) {
      return;
    }

    const fetchTemplates = async () => {
      setIsTemplatesLoading(true);

      try {
        const templatesByConnections = await Promise.all(
          connections.map(({ id }) =>
            getEnterpriseIntegrationTemplates(
              activeEnterprise.id,
              id,
              token,
              integrationProductSlug
            )
          )
        );

        setTemplates(templatesByConnections.flat());
      } catch (error) {
        setTemplatesError(error as Error);
      } finally {
        setIsTemplatesLoading(false);
      }
    };

    fetchTemplates();
  }, [connections, activeEnterprise]);

  return {
    templates,
    isTemplatesLoading,
    templatesError
  };
};

export default useTemplatesForConnections;
